<template>
  <v-footer v-if="isFunBookV2" dark class="wd-py-4 wd-px-0 wd-z-2" :height="isUpMedium ? 80 : shouldFunBookV2TermsHide ? 120 : 160" color="black">
    <FunBookV2Footer :is-up-medium="isUpMedium" :this-year="thisYear" :current-locale="currentLocale" :should-terms-hide="shouldFunBookV2TermsHide" />
  </v-footer>
  <v-footer v-else dark class="page-footer-container py-4 px-0" height="auto" color="black">
    <v-container class="ma-auto py-0 body-container" fluid>
      <component :is="footerComponent" :menu-items="menuItems" />

      <v-row class="align-start mt-3 footer-bottom justify-space-between" no-gutters>
        <div class="copyright d-flex align-end">
          <img class="wd-mr-2 wd-max-w-31 wd-max-h-34px" cover src="@/assets/images/logo/logo.svg?url" />
          <span>&copy; {{ thisYear }} Zoek. All rights reserved.</span>
        </div>

        <div class="d-flex icons">
          <a v-for="(media, idx) in socialMediaItems" :key="idx" :href="media.link" target="_blank">
            <funnow-icon class="icon--social-media" :max-width="isUpMedium ? 40 : 28" :image-path="`${media.iconName}.svg`" />
          </a>
        </div>
      </v-row>
    </v-container>
  </v-footer>
</template>

<script lang="ts">
import { useMounted } from '@vueuse/core'
import tools from '@/mixin/tools'
import { usePlatformStore } from '@/store-pinia/platform'
import { useVuetify } from '@/composition/useUtil'
import { useExternalLinks, usePartnerLinks } from '@/composition/useConst'
import { FAQ_LINK } from '@/constants/links'

import FunnowWeb from '@/components/core/Footer_Web.vue'
import FunnowMobile from '@/components/core/Footer_Mobile.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import FunBookV2Footer from '@/components/core/FunBookV2Footer.vue'

export default defineComponent({
  components: {
    FunnowWeb,
    FunnowMobile,
    FunnowIcon,
    FunBookV2Footer,
  },
  mixins: [tools],
  props: {
    shouldFunBookV2TermsHide: { type: Boolean, default: false },
  },
  setup() {
    const config = useRuntimeConfig()

    const $dayjs = useDayjs()
    const $i18n = useI18n()
    const locale = $i18n.locale
    const localePath = useLocalePath()
    const vuetify = useVuetify()
    const { whyFunNowLink, brandStoryLink } = useExternalLinks()
    const { partnerLink, sassPartnerLink } = usePartnerLinks()

    const platformStore = usePlatformStore()
    const { isFunBookV2 } = storeToRefs(platformStore)

    const isMounted = useMounted()

    const baseUrl = config.public.BASE_URL
    const hiringLink: any = {
      'zh-tw': 'https://www.cakeresume.com/companies/funnow',
      'zh-cn': 'https://funnow.bamboohr.com/careers',
      ja: 'https://www.wantedly.com/companies/company_1773103',
      en: 'https://funnow.bamboohr.com/careers',
      th: 'https://funnow.bamboohr.com/careers',
    }
    const linkMap: any = {
      'zh-tw': { facebook: 'https://bit.ly/32nOu7K', instagram: 'https://bit.ly/31lc6uC' },
      'zh-cn': { facebook: 'https://facebook.com/funnowmalaysia', instagram: 'https://bit.ly/2EtQMdq' },
      ja: { facebook: 'https://bit.ly/32nOu7K', instagram: 'https://bit.ly/2EuLT3P' },
      en: { facebook: 'https://facebook.com/funnowmalaysia', instagram: 'https://bit.ly/2EtQMdq' },
      th: { facebook: 'https://bit.ly/44EyBZe', instagram: 'https://bit.ly/44EyFbq' },
    }

    const isUpMedium = computed(() => vuetify.display.mdAndUp.value)
    const footerComponent = computed(() => (isUpMedium.value && isMounted.value ? FunnowWeb : FunnowMobile))
    const currentLocale = computed(() => $i18n.localeProperties || {})
    const thisYear = computed(() => $dayjs().year())

    const exploresBlock = computed(() => ({
      title: $i18n.t('footer_explore'),
      subItems: [
        {
          title: $i18n.t('footer_why_funnow'),
          url: whyFunNowLink.value,
        },
        {
          title: $i18n.t('footer_brand_story'),
          url: brandStoryLink.value,
        },
        {
          title: $i18n.t('footer_blog'),
          url: 'https://blog.myfunnow.com/blog',
        },
        {
          title: $i18n.t('footer_career'),
          url: hiringLink[locale.value],
        },
      ],
    }))

    const termsBlock = computed(() => {
      const res = {
        title: $i18n.t('footer_term'),
        subItems: [
          {
            title: $i18n.t('footer_user_policy'),
            url: `${baseUrl}${localePath({ name: 'terms-consumer' })}`,
          },
          {
            title: $i18n.t('footer_private_policy'),
            url: `${baseUrl}${localePath({ name: 'terms-privacy' })}`,
          },
          {
            title: $i18n.t('footer_refund_policy'),
            url: `${baseUrl}${localePath({ name: 'terms-refund' })}`,
          },
          ...(locale.value === 'ja'
            ? [
                {
                  title: $i18n.t('footer_japan_term'),
                  url: `${baseUrl}${localePath({ name: 'terms-law' })}`,
                },
              ]
            : []),
        ],
      }

      return res
    })

    const contactBlock = computed(() => ({
      title: $i18n.t('footer_contact_us'),
      subItems: [
        {
          title: $i18n.t('footer_faq'),
          url: FAQ_LINK[locale.value],
        },
        {
          title: $i18n.t('footer_contact_us'),
          url: `${baseUrl}${localePath({ name: 'contact' })}`,
        },
      ],
    }))

    const cooperationBlock = computed(() => {
      const res = {
        title: $i18n.t('footer_work_with_funnow'),
        subItems: [
          {
            title: $i18n.t('footer_partner'),
            url: partnerLink.value,
          },
          ...(sassPartnerLink.value
            ? [
                {
                  title: $i18n.t('footer_why_funbook'),
                  url: sassPartnerLink.value,
                },
              ]
            : []),
          ...(locale.value === 'zh-tw'
            ? [
                {
                  title: $i18n.t('footer_bulk_purchases'),
                  url: `${config.public.EVENT_WEBSITE_URL}/company-welfare`,
                },
              ]
            : []),
        ],
      }

      return res
    })

    const menuItems = computed(() => [exploresBlock.value, termsBlock.value, contactBlock.value, cooperationBlock.value])

    const socialMediaItems = computed(() => [
      { iconName: 'instagram-icon_white', link: linkMap[locale.value].instagram },
      { iconName: 'facebook-icon_white', link: linkMap[locale.value].facebook },
    ])

    return {
      isFunBookV2,
      isUpMedium,
      currentLocale,
      thisYear,
      menuItems,
      socialMediaItems,
      footerComponent,
    }
  },
})
</script>

<style scoped lang="scss" src="@/assets/sass/components/core/footer.scss"></style>
